import React, { useState, useEffect, useMemo } from 'react'
import classNames from 'classnames'
import { isMobile } from 'react-device-detect'
import {
  useAvatarFilters,
  useFilteredAvatars,
} from 'hooks/query/aiStudio/useAvailableAvatars'
import { AvatarType } from 'service/aiStudio'
import { DropdownSelect } from 'components/atoms/DropdownSelect'
import { OptimizedImage } from 'components/atoms/OptimizedImage'

interface ModelSelectionStepProps {
  selectedAvatar: AvatarType | null
  setSelectedAvatar: React.Dispatch<React.SetStateAction<AvatarType | null>>
}

export const ModelSelectionStep: React.FC<ModelSelectionStepProps> = ({
  selectedAvatar,
  setSelectedAvatar,
}) => {
  const [selectedGenders, setSelectedGenders] = useState<string[]>([
    'female',
    'male',
  ])
  const [selectedRegions, setSelectedRegions] = useState<string[]>([
    'local',
    'global',
  ])

  // 필터 옵션 조회
  const { data: filterOptions, isLoading: isLoadingFilters } =
    useAvatarFilters()

  // 전체 아바타 조회 (한 번만 호출)
  const { data: allAvatars, isLoading: isLoadingAvatars } = useFilteredAvatars({
    gender: selectedGenders,
    ethnicity: selectedRegions,
  })

  // 프론트엔드에서 필터링
  const filteredAvatars = useMemo(() => {
    if (!allAvatars) return []

    return allAvatars.filter((avatar) => {
      const genderMatch =
        selectedGenders.length > 0 && selectedGenders.includes(avatar.gender)
      const regionMatch =
        selectedRegions.length > 0 && selectedRegions.includes(avatar.ethnicity)
      return genderMatch && regionMatch
    })
  }, [allAvatars, selectedGenders, selectedRegions])

  const isLoading = isLoadingFilters || isLoadingAvatars

  // 성별 필터 옵션에서 male을 disabled로 설정
  const genderOptions = useMemo(() => {
    if (!filterOptions?.genders) return []

    return filterOptions.genders.map((option) => ({
      ...option,
      // disabled: option.value === 'male',
    }))
  }, [filterOptions?.genders])

  // 첫 번째 아바타 자동 선택
  useEffect(() => {
    if (!isLoading && filteredAvatars && filteredAvatars.length > 0) {
      // 필터가 변경될 때마다 항상 첫 번째 아바타를 선택 (기존 선택 초기화)
      setSelectedAvatar(filteredAvatars[0])
    } else if (filteredAvatars && filteredAvatars.length === 0) {
      // 필터 조건에 맞는 아바타가 없으면 선택 해제
      setSelectedAvatar(null)
    }
  }, [filteredAvatars, isLoading, setSelectedAvatar])

  return (
    <div className="flex flex-col gap-4 overflow-y-auto md:h-[500px]">
      {/* 제목 섹션 */}
      <div className="flex flex-col gap-1 md:gap-2">
        <h2 className="text-[18px] font-bold text-[#2E343D]">패션 모델 선택</h2>
        <p className="text-base text-[#2E343D]">
          내 옷에 잘 어울리는 모델을 선택해보세요!
        </p>
      </div>

      {/* 필터 드롭다운 */}
      <div
        className={classNames(' ', {
          'flex gap-6': !isMobile,
          'flex flex-col gap-3': isMobile,
        })}
      >
        <div className="flex items-center gap-2">
          <span className="text-base font-normal text-[#2E343D]">성별</span>
          <DropdownSelect
            options={genderOptions}
            values={selectedGenders}
            onChange={setSelectedGenders}
            placeholder="성별"
          />
        </div>
        <div className="flex items-center gap-2">
          <span className="text-base font-normal text-[#2E343D]">지역</span>
          <DropdownSelect
            options={filterOptions?.ethnicities || []}
            values={selectedRegions}
            onChange={setSelectedRegions}
            placeholder="지역"
          />
        </div>
      </div>

      {/* 모델 선택 그리드 */}
      {isLoading ? (
        <div className="flex items-center justify-center py-20">
          <div className="text-sm text-[#8F929B]">모델을 불러오는 중...</div>
        </div>
      ) : (
        <div
          className={classNames('gap-[6px]', {
            // 모바일: 2x2 그리드
            'grid grid-cols-2': isMobile,
            // 데스크톱: 가로 배치
            'grid grid-cols-4': !isMobile,
          })}
        >
          {filteredAvatars && filteredAvatars.length > 0 ? (
            filteredAvatars.map((avatar) => {
              const isSelected = selectedAvatar?.id === avatar.id

              return (
                <div
                  key={avatar.id}
                  className="flex cursor-pointer flex-col gap-2"
                  onClick={() => {
                    console.log('selectedAvatar', avatar)
                    setSelectedAvatar(avatar)
                  }}
                >
                  <div
                    className={classNames(
                      'relative overflow-hidden rounded-[10px] border-4 bg-gray-100',
                      {
                        // 모바일: 168px 너비
                        'h-[299px] w-[168px]': isMobile,
                        // 데스크톱: 136px 너비
                        'h-[240px] w-[136px]': !isMobile,
                        'rounded-[14px] border-[#00B7B9]': isSelected,
                        'border-transparent': !isSelected,
                      },
                    )}
                  >
                    <OptimizedImage
                      src={avatar.thumbnailUrl}
                      alt={avatar.name}
                      className="h-full w-full object-cover"
                      enableIntersectionObserver={true}
                      onError={() => {
                        // 이미지 로드 실패시 플레이스홀더 (추후 구현)
                      }}
                    />
                  </div>
                </div>
              )
            })
          ) : (
            <div className="flex w-full items-center justify-center py-20">
              <div className="text-sm text-[#8F929B]">
                선택한 필터 조건에 맞는 모델이 없습니다.
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  )
}
